"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight, CheckCircle, Calendar, Clock } from "lucide-react";
import { motion } from "framer-motion";
import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";
import { BLOGS_DATA } from "@/features/blogs/constants/blogs-data";
import type { BlogPost } from "@/features/blogs/types/blog";

interface HomeBlogPreviewSectionProps {
  locale: AppLocale;
  blogs?: BlogPost[];
}

export function HomeBlogPreviewSection({ locale, blogs: liveBlogs }: HomeBlogPreviewSectionProps) {
  const isHi = locale === "hi";
  const blogs = (liveBlogs && liveBlogs.length > 0 ? liveBlogs : BLOGS_DATA[locale]).slice(0, 4);

  return (
    <section className="relative w-full overflow-hidden bg-[#FAF8F5] py-20 sm:py-28">
      {/* Header */}
      <div className="mx-auto mb-12 max-w-7xl px-6 lg:px-8">
        <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55 }}
              className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5"
            >
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
              <span className="text-xs font-semibold tracking-wider text-primary uppercase">
                {isHi ? "हमारा जर्नल" : "Our Journal"}
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65, delay: 0.08 }}
              className="font-serif text-3xl font-light text-slate-900 sm:text-4xl lg:text-6xl"
            >
              {isHi ? (
                <>
                  अधिक ऊर्जा।
                  <br />
                  अधिक आत्मविश्वास।
                </>
              ) : (
                <>
                  More energy.
                  <br />
                  More confidence.
                </>
              )}
            </motion.h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <Link
              href={localizeHref(locale, "/blogs")}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:scale-105 hover:bg-primary/90"
            >
              {isHi ? "सभी ब्लॉग देखें" : "Explore all blogs"}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </motion.div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-6 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
        {blogs.map((blog) => (
          <Link
            key={blog.slug}
            href={localizeHref(locale, `/blogs/${blog.slug}`)}
            className="group flex min-w-0 flex-col justify-between transition-all duration-500"
          >
            {/* Image */}
            <div className="relative mb-4 aspect-[16/10] w-full overflow-hidden rounded-xl border border-[#EAE5DA]/20 bg-slate-100">
              <Image
                src={blog.imageUrl}
                alt={blog.title}
                fill
                sizes="360px"
                className="object-cover grayscale-[20%] transition-all duration-700 ease-out group-hover:scale-[1.04] group-hover:grayscale-0"
              />
            </div>

            {/* Category + Tags */}
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold tracking-wide text-[#5D5FEF] uppercase">
                {blog.category}
              </span>
              {blog.tags && blog.tags.length > 0 && (
                <>
                  <span className="text-[10px] text-slate-300">•</span>
                  <div className="flex flex-wrap gap-1">
                    {blog.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-medium text-slate-500 normal-case"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Title */}
            <h4 className="mb-2 line-clamp-2 font-heading text-lg leading-snug font-medium text-slate-900 transition-colors duration-300 group-hover:text-[#155A7D]">
              {blog.title}
            </h4>

            {/* Excerpt */}
            <p className="mb-4 line-clamp-2 text-xs leading-relaxed font-light text-slate-600 sm:text-sm">
              {blog.excerpt}
            </p>

            {/* Author row */}
            <div className="flex items-center gap-2 border-t border-[#EAE5DA]/30 pt-3">
              <div className="relative h-6 w-6 shrink-0 overflow-hidden rounded-full border border-[#EAE5DA]">
                <Image
                  src={blog.author.avatarUrl}
                  alt={blog.author.name}
                  fill
                  className="object-cover"
                />
              </div>
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <span className="truncate text-[11px] font-medium text-slate-700">
                  {blog.author.name}
                </span>
                {blog.author.isVerified && (
                  <CheckCircle className="h-3 w-3 shrink-0 fill-[#155A7D] text-[#FAF8F5]" />
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2 text-[10px] font-light text-slate-400">
                <span className="flex items-center gap-0.5">
                  <Calendar className="h-3 w-3" />
                  {blog.date}
                </span>
                <span>·</span>
                <span className="flex items-center gap-0.5">
                  <Clock className="h-3 w-3" />
                  {blog.readTime}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
